import {HttpClient, HttpHeaders} from '@angular/common/http'
import {inject, Service, signal, WritableSignal} from '@angular/core'
import {ISparRequest, ISparResponse} from '@sparbanken-syd/sparbanken-spar'
import {
  AccountApplication,
  AccountRequest,
  ApplicantData,
  SelectableItem,
  Track
} from '@sparbanken-syd/spb-savings-account-backend'
import {Observable} from 'rxjs'
import {environment} from '../../environments/environment'
import {RouteService} from './route.service'

export const incomeOptionsList: SelectableItem[] = [
  {value: 'salary', label: 'Lön', checked: false},
  {value: 'pension', label: 'Pension', checked: false},
  {
    value: 'benefits',
    label: 'Bidrag eller arbetslöshetsersättning',
    checked: false
  },
  {
    value: 'studentAid',
    label: 'Studiebidrag och/eller studielån',
    checked: false
  },
  {
    value: 'soleProprietorship',
    label: 'Inkomst från enskild firma',
    checked: false
  },
  {value: 'capital', label: 'Kapital', checked: false},
  {value: 'other', label: 'Annan', checked: false}
]


@Service()

export class SavingsAccountService {
  public incomeOptions: SelectableItem[]
  public savingsSource: SelectableItem[]

  public testMode = signal(false)
  public accessToken: WritableSignal<string | undefined> = signal(undefined)
  public sessionTimeout = signal(false)

  public applicant = signal({
    name: '', personalNumber: '', countryCode: '', email: '', phone: '',
    city: '', postalCode: '', streetNumber: '', streetAdress: ''
  } as ApplicantData)

  private readonly routeService = inject(RouteService)
  private readonly httpClient = inject(HttpClient)

  constructor() {
    this.incomeOptions = this.getIncomeOptions()
    this.savingsSource = this.getSavingsSource()
  }

  public updateApplicantData(userId: string, iSparResponse?: ISparResponse): void {
    const addresses = iSparResponse?.addresses

    // Find the primary address only if an address list exists.
    // Priority: "Visiting" type. Fallback: The first address in the list.
    const primaryAddress = addresses
      ? addresses.find(a => a.type === 'Visiting') ?? addresses[0]
      : undefined

    const entrance = primaryAddress?.entrance ?? ''
    const apartment = primaryAddress?.apartment ? ` ${primaryAddress.apartment}` : ''

    const applicantData = {
      countryCode: '+46',
      email: '',
      phone: '',
      name: iSparResponse?.name ?? '',
      personalNumber: userId,
      streetAdress: primaryAddress?.streetName ?? '',
      streetNumber: primaryAddress?.streetNumber ? `${primaryAddress.streetNumber}${entrance}${apartment}` : '',
      postalCode: primaryAddress?.postalCode ?? '',
      city: primaryAddress?.city ?? '',
      sparFailed: !iSparResponse,
      hasMultipleAddresses: addresses && addresses.length > 1 // Check if there is more than one address.
    }
    this.applicant.set(applicantData)
  }

  public saveRequest(request: AccountRequest): Observable<AccountApplication> {
    const url = `${environment.requestApi}/request`
    let headers = new HttpHeaders()
    headers = headers.set('Authorization', `Bearer ${this.accessToken()}`)
    return this.httpClient.put<AccountApplication>(url, request, {headers})
  }

  public saveTrack(): Observable<Track> {
    const url = `${environment.requestApi}/track`
    let headers = new HttpHeaders()
    headers = headers.set('Authorization', `Bearer ${this.accessToken()}`)
    return this.httpClient.put<any>(url, {isHorse: this.routeService.onHorseRoute()}, {headers})
  }

  /**
   * Add signature, the only thing we need is the access token.
   * @param accessToken - The token received after sign
   * @param id
   */
  public addSignature(accessToken: string, id: string | null): Observable<AccountApplication> {
    const url = `${environment.requestApi}/user/${id}/sign`
    let headers = new HttpHeaders()
    headers = headers.set('Authorization', `Bearer ${accessToken}`)
    return this.httpClient.put<AccountApplication>(url, {}, {headers})
  }

  public getSpar(sub: string): Observable<ISparResponse> {
    const url = `${environment.requestApi}/spar`
    const input: ISparRequest = {
      source: 'sparbanken-spar-fe',
      // This will be taken from the access token in real life
      sub
    }
    let headers = new HttpHeaders()
    headers = headers.set('Authorization', `Bearer ${this.accessToken()}`)
    return this.httpClient.put<ISparResponse>(url, input, {headers})
  }

  private getIncomeOptions(): SelectableItem[] {
    const options = structuredClone(incomeOptionsList) // Deep copy

    // Add horse before annan
    if (this.routeService.onHorseRoute()) {
      options.splice(options.length - 1, 0, {
        value: 'horse',
        label: 'Försäljning av häst',
        checked: false
      })
    }
    return options
  }

  private getSavingsSource(): SelectableItem[] {
    const baseSources: SelectableItem[] = [
      {value: 'Inkomst med löpande sparande', checked: false},
      {value: 'Arv eller gåva', checked: false},
      {value: 'Försäljning av bostad', checked: false},
      {
        value: 'Annan försäljning (företag eller andra tillgångar)',
        checked: false
      },
      {value: 'Spel eller lotterivinst', checked: false}
    ]

    if (this.routeService.onHorseRoute()) {
      // Insert 'Sale of horse' at index 3 (after property sale)
      baseSources.splice(3, 0, {
        value: 'Försäljning av häst',
        checked: false
      })
    }

    return baseSources
  }
}